import { Controller } from "@hotwired/stimulus";
import localBootstrap from "bootstrap/dist/js/bootstrap";

const bootstrap = new Proxy({}, {
  get(_target, prop) {
    const activeBootstrap =
      typeof window !== "undefined" && window.bootstrap ? window.bootstrap : localBootstrap;
    return activeBootstrap[prop];
  },
});

// Standalone bottom-sheet drag-to-dismiss for modals rendered on pages that do
// not load the full ordering-layout controller (e.g. the checkout boundary
// modal). Mirrors that controller's handle-drag behaviour so the sheet feels the
// same: drag the top handle down past a threshold to dismiss, otherwise spring
// back. Mobile only.
export default class extends Controller {
  connect() {
    // Mirror the menu page's ordering-layout controller: undo Bootstrap's
    // body scroll-lock (overflow:hidden + padding-right) when the modal opens so
    // the native scrollbar stays put. Checkout keeps no reserved gutter, so
    // without this the page either shifts sideways or shows a white strip.
    this.clearScrollLock = this.clearScrollLock.bind(this);
    this.onScrollLockShow = () => queueMicrotask(this.clearScrollLock);

    this.element.addEventListener("show.bs.modal", this.onScrollLockShow);
    this.element.addEventListener("shown.bs.modal", this.clearScrollLock);
    this.element.addEventListener("hide.bs.modal", this.onScrollLockShow);
    this.element.addEventListener("hidden.bs.modal", this.clearScrollLock);
  }

  disconnect() {
    this.element.removeEventListener("show.bs.modal", this.onScrollLockShow);
    this.element.removeEventListener("shown.bs.modal", this.clearScrollLock);
    this.element.removeEventListener("hide.bs.modal", this.onScrollLockShow);
    this.element.removeEventListener("hidden.bs.modal", this.clearScrollLock);
  }

  clearScrollLock() {
    document.body.style.removeProperty("padding-right");
    document.body.style.removeProperty("overflow");

    this.element.style.setProperty("padding", "0");

    this.element.querySelectorAll(".modal-dialog").forEach((dialog) => {
      dialog.style.removeProperty("padding-right");
      dialog.style.removeProperty("padding-left");
      dialog.style.removeProperty("margin-right");
      dialog.style.setProperty("margin-left", "auto");
      dialog.style.setProperty("margin-right", "auto");
    });

    document.querySelectorAll(
      ".sticky-top, .fixed-top, .fixed-bottom, .is-fixed, .oc-sticky-header, .oc-cartbar"
    ).forEach((element) => {
      element.style.removeProperty("padding-right");
      element.style.removeProperty("margin-right");
    });
  }

  isMobileSheet() {
    return window.matchMedia("(max-width: 767.98px)").matches;
  }

  dragStart(event) {
    if (!this.isMobileSheet()) return;
    this.sheet = event.currentTarget.closest(".modal-dialog");
    if (!this.sheet) return;

    this.startY = event.touches[0].clientY;
    this.delta = 0;
    this.sheet.style.transition = "none";
  }

  dragMove(event) {
    if (!this.sheet) return;
    const delta = event.touches[0].clientY - this.startY;
    if (delta <= 0) {
      this.delta = 0;
      this.sheet.style.transform = "";
      return;
    }
    this.delta = delta;
    this.sheet.style.transform = `translateY(${delta}px)`;
  }

  dragEnd() {
    if (!this.sheet) return;
    this.settle(this.sheet, this.delta);
    this.sheet = null;
    this.delta = 0;
  }

  settle(sheet, delta) {
    const dismissed = delta > Math.min(sheet.offsetHeight * 0.25, 160);

    // Restore the CSS-driven transition so spring-back and dismiss both animate.
    sheet.style.transition = "";

    if (dismissed) {
      sheet.style.transform = "translateY(100%)";
      const modal = sheet.closest(".modal");
      if (modal) bootstrap.Modal.getOrCreateInstance(modal).hide();
      const cleanup = () => {
        sheet.style.transform = "";
        sheet.removeEventListener("transitionend", cleanup);
      };
      sheet.addEventListener("transitionend", cleanup, { once: true });
      setTimeout(cleanup, 450);
    } else {
      sheet.style.transform = "";
    }
  }
}
